'use client';

import { Mail, MessageCircle, Calendar, Phone } from 'lucide-react';

const ConsultationPortal = () => {
  const whatsappNumber = '918106668088';
  const email = 'gencarbon.info@gmail.com';

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Hi! I am interested in learning more about GenCarbon\'s Billion Dollar Secret Sauce Opportunities and would like to schedule a consultation.');
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  const handleEmailClick = () => {
    const subject = encodeURIComponent('Billion Dollar Opportunities - Consultation Request');
    const body = encodeURIComponent('Hi GenCarbon Team,\n\nI am interested in exploring your Billion Dollar Secret Sauce Opportunities and would like to schedule a detailed technical consultation.\n\nCompany Details:\nName:\nIndustry:\nCurrent ESG Challenges:\n\nBest time for consultation:\n\nThank you!');
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const consultationTypes = [
    {
      title: 'Executive Overview',
      duration: '30 minutes',
      description: 'High-level overview of offerings and strategic value proposition',
      features: [
        'Market opportunity analysis',
        'Revenue model overview',
        'Competitive advantages',
        'Implementation timeline'
      ]
    },
    {
      title: 'Technical Deep Dive',
      duration: '60 minutes',
      description: 'Detailed technical specifications and implementation capabilities',
      features: [
        'Technology architecture review',
        'Integration requirements',
        'Data security and compliance',
        'Customization options'
      ]
    },
    {
      title: 'ROI Strategy Session',
      duration: '90 minutes',
      description: 'Comprehensive financial modeling and investment analysis',
      features: [
        'Custom ROI calculations',
        'Market sizing and forecasting',
        'Risk assessment',
        'Implementation roadmap'
      ]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-emerald-900 to-teal-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Schedule Your Exclusive Consultation
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Connect with our experts to explore how these opportunities can transform your business
          </p>
        </div>

        {/* Consultation Types */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {consultationTypes?.map((type, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:border-emerald-400 transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-white">{type?.title}</h3>
                <div className="px-3 py-1 bg-emerald-500 rounded-full">
                  <span className="text-sm font-semibold text-white">{type?.duration}</span>
                </div>
              </div>
              <p className="text-white/70 mb-6">{type?.description}</p>
              <ul className="space-y-3">
                {type?.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-white/80 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact Options */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              Choose Your Preferred Contact Method
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* WhatsApp Contact */}
              <button
                onClick={handleWhatsAppClick}
                className="group relative bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-8 hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl"
              >
                <div className="flex items-center gap-4 mb-4">
                  <MessageCircle className="w-8 h-8 text-white" />
                  <div className="text-left">
                    <h4 className="text-xl font-bold text-white">WhatsApp Consultation</h4>
                    <p className="text-white/80 text-sm">Immediate response</p>
                  </div>
                </div>
                <p className="text-white/90 text-sm mb-4">
                  Connect instantly with our experts for quick queries and consultation scheduling
                </p>
                <div className="flex items-center gap-2 text-white font-semibold">
                  <Phone className="w-5 h-5" />
                  <span>+91 81066 68088</span>
                </div>
              </button>

              {/* Email Contact */}
              <button
                onClick={handleEmailClick}
                className="group relative bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-8 hover:from-blue-700 hover:to-cyan-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl"
              >
                <div className="flex items-center gap-4 mb-4">
                  <Mail className="w-8 h-8 text-white" />
                  <div className="text-left">
                    <h4 className="text-xl font-bold text-white">Email Consultation</h4>
                    <p className="text-white/80 text-sm">Detailed discussions</p>
                  </div>
                </div>
                <p className="text-white/90 text-sm mb-4">
                  Send detailed queries and receive comprehensive technical documentation
                </p>
                <div className="flex items-center gap-2 text-white font-semibold text-sm">
                  <Mail className="w-5 h-5" />
                  <span className="break-all">gencarbon.info@gmail.com</span>
                </div>
              </button>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Calendar className="w-5 h-5 text-emerald-400" />
              <span className="text-white font-semibold">Available Monday - Saturday, 9 AM - 6 PM IST</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultationPortal;